import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, TrendingUp, Calendar, Euro, Package, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { AddExpenseDialog } from "@/components/admin/AddExpenseDialog";
import { getBackendUrl } from "@/utils/apiBase";

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  equipment: "Équipement",
  transport: "Transport",
  marketing: "Marketing",
  maintenance: "Maintenance",
  other: "Autre"
};

const MONTH_NAMES = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];

export const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, expensesRes] = await Promise.all([
        axios.get(`${API}/statistics`),
        axios.get(`${API}/expenses`)
      ]);
      setStats(statsRes.data);
      setExpenses(expensesRes.data);
    } catch (error) {
      console.error("Erreur lors du chargement des données", error);
      toast.error("Erreur lors du chargement des statistiques");
    } finally {
      setLoading(false);
    }
  };

  const handleAddExpense = async (expenseData) => {
    try {
      await axios.post(`${API}/expenses`, expenseData);
      toast.success("Charge ajoutée avec succès");
      setExpenseDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error("Erreur lors de l'ajout", error);
      toast.error("Erreur lors de l'ajout de la charge");
    }
  };

  const handleUpdateExpense = async (expenseData) => {
    try {
      await axios.put(`${API}/expenses/${editingExpense.id}`, expenseData);
      toast.success("Charge modifiée avec succès");
      setExpenseDialogOpen(false);
      setEditingExpense(null);
      fetchData();
    } catch (error) {
      console.error("Erreur lors de la modification", error);
      toast.error("Erreur lors de la modification de la charge");
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette charge ?")) return;
    
    try {
      await axios.delete(`${API}/expenses/${expenseId}`);
      toast.success("Charge supprimée avec succès");
      fetchData();
    } catch (error) {
      console.error("Erreur lors de la suppression", error);
      toast.error("Erreur lors de la suppression de la charge");
    }
  };

  const openAddDialog = () => {
    setEditingExpense(null);
    setExpenseDialogOpen(true);
  };

  const openEditDialog = (expense) => {
    setEditingExpense(expense);
    setExpenseDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-xl text-gray-600">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-[#f0e6d3] rounded-xl bg-white hover:shadow-md transition-shadow">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-[#999] font-['DM_Sans']">Chiffre d'Affaires</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-[#1a1a1a] font-['Playfair_Display']">{stats.total_revenue.toFixed(2)} €</div>
            <p className="text-xs text-[#b8977e] mt-1 font-medium">Réservations payées et confirmées</p>
          </CardContent>
        </Card>

        <Card className={`border-[#f0e6d3] rounded-xl bg-white hover:shadow-md transition-shadow`}>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-[#999] font-['DM_Sans']">Bénéfice Net</CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-3xl font-bold font-['Playfair_Display'] ${stats.net_profit >= 0 ? 'text-[#1a1a1a]' : 'text-red-600'}`}>
              {stats.net_profit.toFixed(2)} €
            </div>
            <p className={`text-xs mt-1 font-medium ${stats.profit_margin >= 0 ? 'text-[#b8977e]' : 'text-red-500'}`}>
              Marge: {stats.profit_margin.toFixed(1)}%
            </p>
          </CardContent>
        </Card>

        <Card className="border-[#f0e6d3] rounded-xl bg-white hover:shadow-md transition-shadow">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-[#999] font-['DM_Sans']">Total Réservations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-[#1a1a1a] font-['Playfair_Display']">{stats.total_bookings}</div>
            <div className="flex gap-3 mt-2 text-xs font-['DM_Sans'] font-medium">
              <span className="text-[#b8977e]">⏳ {stats.bookings_by_status.pending}</span>
              <span className="text-blue-600">✓ {stats.bookings_by_status.confirmed}</span>
              <span className="text-green-600">✓ {stats.bookings_by_status.paid}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#f0e6d3] rounded-xl bg-white hover:shadow-md transition-shadow">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-[#999] font-['DM_Sans']">Panier Moyen</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-[#1a1a1a] font-['Playfair_Display']">{stats.average_booking_value.toFixed(2)} €</div>
            <p className="text-xs text-[#b8977e] mt-1 font-medium">Par réservation</p>
          </CardContent>
        </Card>
      </div>

      {/* Advanced KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-[#f0e6d3] rounded-xl bg-white hover:shadow-md transition-shadow">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-[#999] font-['DM_Sans']">Taux de Conversion</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-[#1a1a1a] font-['Playfair_Display']">{stats.conversion_rate.toFixed(1)}%</div>
            <p className="text-xs text-[#b8977e] mt-1 font-medium">{stats.confirmed_bookings} / {stats.total_quotes} devis</p>
          </CardContent>
        </Card>

        <Card className="border-[#f0e6d3] rounded-xl bg-white hover:shadow-md transition-shadow">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-[#999] font-['DM_Sans']">Taux d'Options</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-[#1a1a1a] font-['Playfair_Display']">{stats.options_rate.toFixed(1)}%</div>
            <p className="text-xs text-[#b8977e] mt-1 font-medium">{stats.bookings_with_options} résa avec add-ons</p>
          </CardContent>
        </Card>

        <Card className="border-[#f0e6d3] rounded-xl bg-white hover:shadow-md transition-shadow">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-[#999] font-['DM_Sans']">Remplissage Mensuel</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-[#1a1a1a] font-['Playfair_Display']">{stats.fill_rate.toFixed(1)}%</div>
            <p className="text-xs text-[#b8977e] mt-1 font-medium">{stats.booked_days_this_month} / {stats.days_in_month} jours</p>
          </CardContent>
        </Card>

        <Card className="border-[#f0e6d3] rounded-xl bg-white hover:shadow-md transition-shadow">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-[#999] font-['DM_Sans']">Durée Moyenne</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-[#1a1a1a] font-['Playfair_Display']">{stats.average_duration.toFixed(1)}h</div>
            <p className="text-xs text-[#b8977e] mt-1 font-medium">Par location</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Réservations par Forfait */}
        <Card className="border-[#f0e6d3] rounded-xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-['Playfair_Display']">
              <Package className="h-5 w-5 text-[#b8977e]" />
              Réservations par Forfait
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Forfait 3h (270€)</span>
                <div className="flex items-center gap-3">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-[#b8977e] h-2 rounded-full"
                      style={{ width: `${(stats.bookings_by_package['3h'] / stats.total_bookings) * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-bold w-8 text-right">{stats.bookings_by_package['3h']}</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Forfait 5h (340€)</span>
                <div className="flex items-center gap-3">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-500 h-2 rounded-full"
                      style={{ width: `${(stats.bookings_by_package['5h'] / stats.total_bookings) * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-bold w-8 text-right">{stats.bookings_by_package['5h']}</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Forfait 7h (400€)</span>
                <div className="flex items-center gap-3">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-500 h-2 rounded-full"
                      style={{ width: `${(stats.bookings_by_package['7h'] / stats.total_bookings) * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-bold w-8 text-right">{stats.bookings_by_package['7h']}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Prochains Événements */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-blue-600" />
              Prochains Événements (30j)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <div className="text-5xl font-bold text-blue-600">{stats.upcoming_events_count}</div>
              <p className="text-sm text-gray-600 mt-2">Événements à venir</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* New Advanced Analytics Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Répartition des Paiements */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-semibold">Répartition des Paiements</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  <span className="text-sm">Stripe</span>
                </div>
                <span className="text-sm font-bold">{stats.stripe_percentage.toFixed(1)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all"
                  style={{ width: `${stats.stripe_percentage}%` }}
                ></div>
              </div>
              
              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-sm">WhatsApp</span>
                </div>
                <span className="text-sm font-bold">{stats.whatsapp_percentage.toFixed(1)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-500 h-2 rounded-full transition-all"
                  style={{ width: `${stats.whatsapp_percentage}%` }}
                ></div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Croissance Mensuelle */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-semibold">Croissance Mensuelle</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-4">
              <div className={`text-4xl font-bold ${stats.growth_rate >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {stats.growth_rate >= 0 ? '+' : ''}{stats.growth_rate.toFixed(1)}%
              </div>
              <p className="text-xs text-gray-600 mt-2">vs mois précédent</p>
              <div className="mt-4 space-y-1">
                <div className="flex justify-between text-xs">
                  <span>Mois actuel</span>
                  <span className="font-semibold">{stats.current_month_revenue.toFixed(0)}€</span>
                </div>
                <div className="flex justify-between text-xs text-gray-500">
                  <span>Mois précédent</span>
                  <span>{stats.previous_month_revenue.toFixed(0)}€</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Top 3 Jours de Semaine */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-semibold">Jours les Plus Demandés</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.top_weekdays && stats.top_weekdays.length > 0 ? (
                stats.top_weekdays.map((item, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`text-lg ${index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}`}>
                        {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                      </span>
                      <span className="text-sm font-medium">{item.day}</span>
                    </div>
                    <span className="text-sm font-bold text-[#b8977e]">{item.count} résa</span>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">Pas assez de données</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-green-600" />
            Chiffre d'Affaires par Mois ({new Date().getFullYear()})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end justify-between gap-2 h-48">
            {Object.entries(stats.revenue_by_month).map(([month, revenue]) => {
              const maxRevenue = Math.max(...Object.values(stats.revenue_by_month));
              const height = maxRevenue > 0 ? (revenue / maxRevenue) * 100 : 0;
              return (
                <div key={month} className="flex flex-col items-center flex-1">
                  <div className="text-xs font-semibold mb-1">{revenue > 0 ? `${revenue.toFixed(0)}€` : ''}</div>
                  <div
                    className="w-full bg-[#b8977e] rounded-t"
                    style={{ height: `${height}%`, minHeight: revenue > 0 ? '8px' : '0' }}
                  ></div>
                  <div className="text-xs text-gray-600 mt-2">{MONTH_NAMES[parseInt(month) - 1]}</div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Expenses Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Euro className="h-5 w-5 text-red-600" />
              Gestion des Charges
            </CardTitle>
            <Button
              onClick={openAddDialog}
              className="bg-[#b8977e] hover:bg-[#a68770]"
            >
              <Plus className="mr-2 h-4 w-4" />
              Ajouter une Charge
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-6 grid grid-cols-2 md:grid-cols-5 gap-4">
            {Object.entries(stats.expenses_by_category).map(([category, amount]) => (
              <div key={category} className="bg-gray-50 rounded-lg p-3 border">
                <div className="text-xs text-gray-600">{CATEGORY_LABELS[category]}</div>
                <div className="text-lg font-bold text-red-600">{amount.toFixed(2)} €</div>
              </div>
            ))}
            <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-lg p-3 border-2 border-red-200">
              <div className="text-xs text-red-700 font-semibold">TOTAL</div>
              <div className="text-lg font-bold text-red-900">{stats.total_expenses.toFixed(2)} €</div>
            </div>
          </div>

          <div className="space-y-2">
            {expenses.length === 0 ? (
              <p className="text-center text-gray-500 py-8">Aucune charge enregistrée</p>
            ) : (
              expenses.map((expense) => (
                <div
                  key={expense.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <span className="font-semibold">{expense.title}</span>
                      <span className="text-xs px-2 py-1 bg-gray-200 rounded">
                        {CATEGORY_LABELS[expense.category]}
                      </span>
                    </div>
                    {expense.description && (
                      <p className="text-sm text-gray-600 mt-1">{expense.description}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">{expense.date}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold text-red-600">{expense.amount.toFixed(2)} €</span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditDialog(expense)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleDeleteExpense(expense.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      <AddExpenseDialog
        open={expenseDialogOpen}
        onClose={() => {
          setExpenseDialogOpen(false);
          setEditingExpense(null);
        }}
        onSave={editingExpense ? handleUpdateExpense : handleAddExpense}
        editingExpense={editingExpense}
      />
    </div>
  );
};
